// ABOUTME: Calendar grid for managing per-date away schedule overrides.
// ABOUTME: Allows clicking days to toggle away/present status, with visual distinction for recurring vs manual overrides.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ArrowBack, ArrowForward, FlightTakeoff as AwayIcon, Check as PresentIcon } from '@mui/icons-material';
import type { FamilyAwaySchedule } from '../types/api';
import { getTodayInFamilyTimezone } from '../utils/dateUtils';

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const CELL_HEIGHT = 40;

interface AwayScheduleCalendarProps {
  schedule: FamilyAwaySchedule;
  loading: boolean;
  onToggleDate: (date: string, newIsAway: boolean | null) => void;
  // For past days, actual isAway values from DailyProgress records (date string → isAway)
  pastDayStatus?: Map<string, boolean>;
  // Called when the visible month changes so the parent can fetch progress data
  onMonthChange?: (monthStart: string, monthEnd: string) => void;
  familyTimezone?: string;
}

const AwayScheduleCalendar: React.FC<AwayScheduleCalendarProps> = ({
  schedule,
  loading,
  onToggleDate,
  pastDayStatus,
  onMonthChange,
  familyTimezone,
}) => {
  const theme = useTheme();
  const today = useMemo(() => {
    const [y, m, d] = getTodayInFamilyTimezone(familyTimezone).split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [familyTimezone]);
  const [currentViewDate, setCurrentViewDate] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1)
  );

  // Build override lookup: date string → isAway boolean
  const overrideMap = useMemo(() => {
    const map = new Map<string, boolean>();
    schedule.overrides.forEach(o => map.set(o.date, o.isAway));
    return map;
  }, [schedule.overrides]);

  // Generate days for the current month
  const days = useMemo(() => {
    const year = currentViewDate.getFullYear();
    const month = currentViewDate.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const result: Date[] = [];
    for (let d = 1; d <= lastDay; d++) {
      result.push(new Date(year, month, d, 12, 0, 0));
    }
    return result;
  }, [currentViewDate]);

  // Editability: today, future, and past 7 days
  const editableCutoff = useMemo(() => {
    const cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    cutoff.setDate(cutoff.getDate() - 7);
    return cutoff;
  }, [today]);

  const isEditable = useCallback((date: Date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return dateOnly >= editableCutoff;
  }, [editableCutoff]);

  const toDateKey = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  // Notify parent when visible month changes so it can fetch progress data
  useEffect(() => {
    if (!onMonthChange) return;
    const year = currentViewDate.getFullYear();
    const month = currentViewDate.getMonth();
    const start = toDateKey(new Date(year, month, 1, 12));
    const end = toDateKey(new Date(year, month + 1, 0, 12));
    onMonthChange(start, end);
  }, [currentViewDate, onMonthChange]);

  const isPast = useCallback((date: Date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dateOnly < todayOnly;
  }, [today]);

  const getDayState = useCallback((date: Date): 'away-recurring' | 'away-override' | 'present-override' | 'normal' | 'away-historical' => {
    const dateKey = toDateKey(date);

    // For past days, use actual DailyProgress data if available
    if (isPast(date) && pastDayStatus) {
      if (pastDayStatus.has(dateKey)) {
        return pastDayStatus.get(dateKey) ? 'away-historical' : 'normal';
      }
      // No progress record for this date — fall through to schedule-based logic
    }

    const dayOfWeek = date.getDay();
    const hasOverride = overrideMap.has(dateKey);
    const isRecurringAway = schedule.recurringDaysOfWeek.includes(dayOfWeek);

    if (hasOverride) {
      return overrideMap.get(dateKey) ? 'away-override' : 'present-override';
    }
    if (isRecurringAway) {
      return 'away-recurring';
    }
    return 'normal';
  }, [overrideMap, schedule.recurringDaysOfWeek, isPast, pastDayStatus]);

  const handleDayClick = useCallback((date: Date) => {
    if (loading || !isEditable(date)) return;

    const dateKey = toDateKey(date);
    const state = getDayState(date);

    switch (state) {
      case 'normal':
        // Mark as away
        onToggleDate(dateKey, true);
        break;
      case 'away-recurring':
        // Override to present
        onToggleDate(dateKey, false);
        break;
      case 'away-override':
      case 'present-override':
        // Remove override (fall back to recurring rule)
        onToggleDate(dateKey, null);
        break;
    }
  }, [loading, isEditable, getDayState, onToggleDate]);

  const navigatePrevious = useCallback(() => {
    setCurrentViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const navigateNext = useCallback(() => {
    setCurrentViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  // Don't allow navigating more than 2 months into the future
  const canNavigateNext = useMemo(() => {
    const maxMonth = new Date(today.getFullYear(), today.getMonth() + 2, 1);
    const nextMonth = new Date(currentViewDate.getFullYear(), currentViewDate.getMonth() + 1, 1);
    return nextMonth <= maxMonth;
  }, [currentViewDate, today]);

  const monthLabel = currentViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const firstDayOffset = days.length > 0 ? days[0].getDay() : 0;

  return (
    <Box>
      {/* Month navigation */}
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
        <IconButton onClick={navigatePrevious} aria-label="Previous month" size="small">
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" sx={{ mx: 2, minWidth: 140, textAlign: 'center' }}>
          {monthLabel}
        </Typography>
        <IconButton onClick={navigateNext} disabled={!canNavigateNext} aria-label="Next month" size="small">
          <ArrowForward fontSize="small" />
        </IconButton>
      </Box>

      {/* Day-of-week headers */}
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5}>
        {DAYS_OF_WEEK.map((day, index) => (
          <Box
            key={`header-${index}`}
            textAlign="center"
            role="columnheader"
            aria-label={['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]}
          >
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              {day}
            </Typography>
          </Box>
        ))}

        {/* Empty cells for alignment */}
        {Array.from({ length: firstDayOffset }, (_, i) => (
          <Box key={`empty-${i}`} sx={{ height: CELL_HEIGHT }} />
        ))}

        {/* Day cells */}
        {days.map(date => {
          const dateKey = toDateKey(date);
          const state = getDayState(date);
          const editable = isEditable(date);
          const isToday = date.toDateString() === today.toDateString();
          const dayOfMonth = date.getDate();

          const isAway = state === 'away-recurring' || state === 'away-override' || state === 'away-historical';
          const isOverride = state === 'away-override' || state === 'present-override';

          // All away states use the same dark blue for clear visual distinction
          const bgColor = isAway ? theme.palette.info.dark
            : state === 'present-override' ? 'white'
            : 'transparent';

          const textColor = isAway ? 'white' : 'text.primary';
          const borderColor = state === 'present-override' ? theme.palette.success.main
            : isToday ? theme.palette.info.main
            : theme.palette.grey[300];
          const borderWidth = state === 'present-override' || isToday ? 2 : 1;
          const opacity = editable || isAway ? 1 : 0.4;

          const stateLabel = state === 'away-historical' ? ', was away'
            : state === 'away-recurring' ? ', away (recurring)'
            : state === 'away-override' ? ', away (override)'
            : state === 'present-override' ? ', present (override)'
            : '';

          return (
            <Box
              key={`day-${dateKey}`}
              component={editable ? 'button' : 'div'}
              role={editable ? 'button' : 'gridcell'}
              aria-label={`${date.toLocaleDateString('en-US', { month: 'long' })} ${dayOfMonth}${isToday ? ', today' : ''}${stateLabel}${!editable ? ', locked' : ''}`}
              onClick={editable ? () => handleDayClick(date) : undefined}
              sx={{
                height: CELL_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                borderRadius: 1,
                border: `${borderWidth}px solid ${borderColor}`,
                backgroundColor: bgColor,
                opacity,
                cursor: editable && !loading ? 'pointer' : 'default',
                p: 0,
                outline: 'none',
                // Reset button styles
                ...(editable ? {
                  background: bgColor,
                  fontFamily: 'inherit',
                  '&:hover': {
                    backgroundColor: editable ? (isAway ? theme.palette.info.main : alpha(theme.palette.info.main, 0.1)) : bgColor,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.info.main}`,
                    outlineOffset: 1,
                  },
                } : {}),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  // Conditional emphasis for today's date only; 600 when bolded, 'normal' (400) otherwise
                  fontWeight: isToday ? 600 : 'normal',
                  color: textColor,
                  fontSize: '0.75rem',
                }}
              >
                {dayOfMonth}
              </Typography>

              {/* Away icon */}
              {isAway && (
                <AwayIcon
                  sx={{
                    position: 'absolute',
                    top: 1,
                    right: 1,
                    fontSize: 10,
                    color: alpha(theme.palette.common.white, 0.9),
                  }}
                />
              )}

              {/* Present override checkmark */}
              {state === 'present-override' && (
                <PresentIcon
                  sx={{
                    position: 'absolute',
                    top: 1,
                    right: 1,
                    fontSize: 10,
                    color: theme.palette.success.main,
                  }}
                />
              )}

              {/* Override dot — distinguishes manual overrides from recurring/historical */}
              {isOverride && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    backgroundColor: isAway ? alpha(theme.palette.common.white, 0.8) : theme.palette.success.main,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: theme.palette.info.dark }} />
          <Typography variant="caption" color="text.secondary">Away</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: 'white', border: `2px solid ${theme.palette.success.main}` }} />
          <Typography variant="caption" color="text.secondary">Present (override)</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AwayScheduleCalendar;
